import { useCallback, useState } from 'react';

import { Box } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import FilterTagsV2 from 'components/TagsV2/FilterTagsV2';
import dayjsService from 'services/dayjs';
import { useAppDispatch } from 'store';
import {
  fetchAtendimentosFinalizadosDoContato,
  resetAtendimentosFinalizadosDoContato,
} from 'store/reducers/atendimentos';
import { SelectChannelInterface } from 'store/reducers/canais';
import { TagInterface } from 'store/reducers/tags';

import ChannelSelectField from './ChannelSelectField';
import RangeDateFilter from './RangeDateFilter';
import { PayloadType } from './TabPanelAtividadesContato';

export interface FilterOptions {
  rangeDate: MaterialUiPickersDate[];
  tags: TagInterface[];
  channelCode: string;
}

interface FilterOptionsProps {
  contatoId: string;
  onFilterChange: (filters: FilterOptions) => void;
}

export default function FilterOptions({
  contatoId,
  onFilterChange,
}: FilterOptionsProps) {
  const dispatch = useAppDispatch();

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    rangeDate: [],
    tags: [],
    channelCode: '',
  });

  const dispatchNewFilter = useCallback(
    (newFilters: FilterOptions) => {
      dispatch(resetAtendimentosFinalizadosDoContato());
      onFilterChange(newFilters);

      const { rangeDate, tags, channelCode } = newFilters;

      const params: PayloadType = {
        contatoId,
        dateFinishedStart: rangeDate[0]
          ? dayjsService(rangeDate[0]).format('YYYY-MM-DD')
          : undefined,
        dateFinishedEnd: rangeDate[1]
          ? dayjsService(rangeDate[1]).format('YYYY-MM-DD')
          : undefined,
        tagIds: tags.map(tag => tag.id) as string[],
      };

      if (channelCode !== '') {
        params.channelCodes = [channelCode];
      }

      dispatch(
        fetchAtendimentosFinalizadosDoContato({
          ...params,
        }),
      );
    },
    [contatoId, dispatch, onFilterChange],
  );

  const handleFilterRangeDate = useCallback(
    (rangeDates: MaterialUiPickersDate[]) => {
      const newFilters = { ...filterOptions, rangeDate: rangeDates };
      setFilterOptions(newFilters);
      dispatchNewFilter(newFilters);
    },
    [filterOptions, dispatchNewFilter],
  );

  const handleResetRangeDate = useCallback(() => {
    const newFilters = { ...filterOptions, rangeDate: [] };
    setFilterOptions(newFilters);
    dispatchNewFilter(newFilters);
  }, [filterOptions, dispatchNewFilter]);

  const handleChannelChange = useCallback(
    (canal: SelectChannelInterface) => {
      const newChannelCode = canal.code;
      const newFilters = { ...filterOptions, channelCode: newChannelCode };
      setFilterOptions(newFilters);
      dispatchNewFilter(newFilters);
    },
    [filterOptions, dispatchNewFilter],
  );

  const handleFilterTags = useCallback(
    (tags: TagInterface[]) => {
      const newFilters = { ...filterOptions, tags };
      setFilterOptions(newFilters);
      dispatchNewFilter(newFilters);
    },
    [filterOptions, dispatchNewFilter],
  );

  const handleResetChannelCode = useCallback(() => {
    const newFilters = { ...filterOptions, channelCode: '' };
    setFilterOptions(newFilters);
    dispatchNewFilter(newFilters);
  }, [filterOptions, dispatchNewFilter]);

  return (
    <Box
      display="flex"
      alignItems="center"
      style={{ flexWrap: 'wrap', rowGap: 16 }}
      gridGap={8}
    >
      <RangeDateFilter
        rangeDate={filterOptions.rangeDate}
        onFilterRangeDate={handleFilterRangeDate}
        onResetRangeDate={handleResetRangeDate}
      />

      <Box minWidth={200}>
        <ChannelSelectField
          value={filterOptions.channelCode}
          onFilter={handleChannelChange}
          onReset={handleResetChannelCode}
        />
      </Box>

      <Box flex={1}>
        <FilterTagsV2
          handleFilter={handleFilterTags}
          values={filterOptions.tags}
        />
      </Box>
    </Box>
  );
}
